import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

export const fetchClients = createAsyncThunk("clients/fetchClients", async ({ page = 1, pageSize = 10 } = {}, { rejectWithValue }) => {
  try {
    const r = await api.get('/Clients', { params: { page, pageSize } });
    return r.data; // PagedResult
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message || "Erreur de connexion");
  }
});

export const createClient = createAsyncThunk("clients/createClient", async (clientData, { rejectWithValue }) => {
  try {
    const payload = {
      nom: clientData.nom,
      telephone: clientData.telephone,
      ville: clientData.ville,
      nationalite: parseInt(clientData.nationalite) || 1,
      cin: clientData.cin || null,
      passeport: clientData.passeport || null,
      numeroPermis: clientData.numeroPermis || null,
    };
    const created = await api.post("/Clients", payload);
    const list = await api.get('/Clients', { params: { page: 1, pageSize: 10 } });
    return { ...list.data, id: created.data }; // PagedResult + id du client créé
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message || "Erreur lors de la création");
  }
});

export const updateClient = createAsyncThunk("clients/updateClient", async ({ id, clientData }, { rejectWithValue }) => {
  try {
    const payload = {
      nom: clientData.nom,
      telephone: clientData.telephone,
      ville: clientData.ville,
      nationalite: parseInt(clientData.nationalite) || 1,
      cin: clientData.cin || null,
      passeport: clientData.passeport || null,
      numeroPermis: clientData.numeroPermis || null,
    };
    await api.patch(`/Clients/${id}`, payload);
    return { id, ...clientData };
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message || "Erreur lors de la modification");
  }
});

export const deleteClient = createAsyncThunk("clients/deleteClient", async (id, { rejectWithValue }) => {
  try {
    await api.delete(`/Clients/${id}`);
    return id;
  } catch (error) {
    const code = error.response?.data?.code;
    return rejectWithValue(code || error.response?.data?.message || error.message || "Erreur lors de la suppression");
  }
});

export const checkClientLinks = createAsyncThunk("clients/checkClientLinks", async (id, { rejectWithValue }) => {
  try { const r = await api.get(`/Clients/${id}/check-links`); return r.data; }
  catch (error) { return rejectWithValue(error.message); }
});

export const archiveClient = createAsyncThunk("clients/archiveClient", async (id, { rejectWithValue }) => {
  try { await api.patch(`/Clients/${id}/archive`); return id; }
  catch (error) { return rejectWithValue(error.message || "Erreur lors de l'archivage"); }
});

export const fetchArchivedClients = createAsyncThunk("clients/fetchArchivedClients", async (_, { rejectWithValue }) => {
  try { const r = await api.get('/Clients/archived'); return r.data; }
  catch (error) { return rejectWithValue(error.message || "Erreur chargement archivés"); }
});

export const blacklistClient = createAsyncThunk("clients/blacklistClient", async (id, { rejectWithValue, getState }) => {
  try {
    await api.patch(`/Clients/${id}/blacklist`);
    const client = getState().clients.items.find(c => c.id === id);
    return client ? { ...client, estBlackliste: true } : { id, estBlackliste: true };
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message || "Erreur blacklist");
  }
});

export const unblockClient = createAsyncThunk("clients/unblockClient", async (id, { rejectWithValue, getState }) => {
  try {
    await api.patch(`/Clients/${id}/debloquer`);
    const client = getState().clients.items.find(c => c.id === id);
    return client ? { ...client, estBlackliste: false } : { id, estBlackliste: false };
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || error.message || "Erreur déblocage");
  }
});

export const getBlacklistedClients = createAsyncThunk("clients/getBlacklistedClients", async (_, { rejectWithValue }) => {
  try {
    const response = await api.get('/Clients/blacklistes');
    return response.data;
  } catch (error) {
    return rejectWithValue(error.message || "Erreur récupération blacklistés");
  }
});

export const searchClientByNom = createAsyncThunk("clients/searchClientByNom", async (nom, { rejectWithValue }) => {
  try {
    const response = await api.get(`/Clients/recherche/${encodeURIComponent(nom)}`);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.message || "Erreur recherche client");
  }
});

const initialState = {
  items: [],
  totalCount: 0,
  totalPages: 1,
  currentPage: 1,
  pageSize: 10,
  archivedItems: [],
  loading: false,
  error: null,
  fetchError: null,
  success: false,
};

const clientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    resetSuccess: (state) => { state.success = false; },
    clearError: (state) => { state.error = null; state.fetchError = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClients.pending, (state) => { state.loading = true; state.fetchError = null; })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.page;
      })
      .addCase(fetchClients.rejected, (state, action) => { state.loading = false; state.fetchError = action.payload; });
    builder
      .addCase(createClient.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createClient.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
        state.currentPage = 1;
        state.success = true;
      })
      .addCase(createClient.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
    builder
      .addCase(updateClient.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(updateClient.fulfilled, (state, action) => {
        state.loading = false;
        const idx = state.items.findIndex(c => c.id === action.payload.id);
        if (idx !== -1) state.items[idx] = { ...state.items[idx], ...action.payload };
        state.success = true;
      })
      .addCase(updateClient.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
    builder
      .addCase(deleteClient.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(deleteClient.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(c => c.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.success = true;
      })
      .addCase(deleteClient.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
    builder
      .addCase(blacklistClient.fulfilled, (state, action) => { const idx = state.items.findIndex(c => c.id === action.payload.id); if (idx !== -1) state.items[idx] = action.payload; state.success = true; })
      .addCase(blacklistClient.rejected, (state, action) => { state.error = action.payload; });
    builder
      .addCase(unblockClient.fulfilled, (state, action) => { const idx = state.items.findIndex(c => c.id === action.payload.id); if (idx !== -1) state.items[idx] = action.payload; state.success = true; })
      .addCase(unblockClient.rejected, (state, action) => { state.error = action.payload; });
    builder
      .addCase(getBlacklistedClients.fulfilled, (state, action) => { state.loading = false; state.items = action.payload; })
      .addCase(getBlacklistedClients.rejected, (state, action) => { state.fetchError = action.payload; });
    builder
      .addCase(searchClientByNom.fulfilled, (state, action) => { state.loading = false; state.items = action.payload; })
      .addCase(searchClientByNom.rejected, (state, action) => { state.fetchError = action.payload; });
    builder
      .addCase(archiveClient.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(c => c.id !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);
        state.success = true;
      })
      .addCase(archiveClient.rejected, (state, action) => { state.loading = false; state.error = action.payload; });
    builder
      .addCase(fetchArchivedClients.pending, (state) => { state.loading = true; state.fetchError = null; })
      .addCase(fetchArchivedClients.fulfilled, (state, action) => { state.loading = false; state.archivedItems = action.payload; })
      .addCase(fetchArchivedClients.rejected, (state, action) => { state.loading = false; state.fetchError = action.payload; });
  },
});

export const { resetSuccess, clearError } = clientsSlice.actions;
export default clientsSlice.reducer;
